import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Chip,
} from '@mui/material';
import {
  PlayArrow as StartIcon,
  Analytics as AnalyticsIcon,
} from '@mui/icons-material';
import { paaAnalysisService } from '../../services/paaAnalysisService';

interface PAABatchAnalysisProps {
  websiteId: string;
}

const PAABatchAnalysis: React.FC<PAABatchAnalysisProps> = ({ websiteId }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const handleStartBatch = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await paaAnalysisService.analyzeBatch(websiteId, 50);
      
      if (response.success) {
        setResult(response);
      } else {
        setError(response.error || 'Batch analysis failed');
      }
    } catch (err: any) {
      setError('Failed to start batch analysis');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        startIcon={<AnalyticsIcon />}
        onClick={() => setOpen(true)}
        color="secondary"
      >
        Batch Content Analysis
      </Button>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Batch PAA Content Analysis
        </DialogTitle>

        <DialogContent>
          {!result && !loading && (
            <Box>
              <Typography paragraph>
                Analyze content gaps across multiple URLs with PAA questions.
              </Typography>
              
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    What this does:
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText 
                        primary="✓ Analyzes up to 50 URLs with PAA questions"
                        secondary="Checks which questions are answered in content"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="✓ Identifies content gaps"
                        secondary="Categorizes by type: pricing, hours, location, etc."
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="✓ Prioritizes improvements"
                        secondary="Rates gaps as high, medium, or low importance"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="✓ Suggests content additions"
                        secondary="Provides specific recommendations for each gap"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>

              <Alert severity="info">
                This will use the Claude Batch API for cost-effective processing.
                Results typically available within 5-10 minutes.
              </Alert>
            </Box>
          )}

          {loading && (
            <Box py={3}>
              <LinearProgress />
              <Typography variant="body1" align="center" sx={{ mt: 2 }}>
                Submitting batch for analysis...
              </Typography>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {result && (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                Batch submitted successfully!
              </Alert>
              
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Batch Details
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography color="text.secondary">Batch ID:</Typography>
                      <Typography fontFamily="monospace" fontSize="small">
                        {result.batchId}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography color="text.secondary">URLs to analyze:</Typography>
                      <Typography>{result.urlCount}</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography color="text.secondary">Status:</Typography>
                      <Chip label={result.status} size="small" color="primary" />
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                The analysis will run in the background. Results will be available
                in the PAA Trees view once processing is complete.
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
          {!result && !loading && (
            <Button
              variant="contained"
              startIcon={<StartIcon />}
              onClick={handleStartBatch}
              disabled={loading}
            >
              Start Analysis
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PAABatchAnalysis;